"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { useRouter, useParams } from "next/navigation";
import { useForm, Controller } from "react-hook-form";
import { ArrowLeft, Plus, Trash2, ImageIcon, FileIcon, Music, Video, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface MediaItem {
  baseurl?: string;
  fileservername?: string;
  title?: string;
  description?: string;
  img_status?: number; // 1 = thumbnail flag (images only)
  /** full URL convenience — used for display when baseurl+fileservername are set */
  _displayUrl?: string;
}

interface BlogFormValues {
  title: string;
  priority: string;
  description: string;
  images: MediaItem[];
  files: MediaItem[];
  audios: MediaItem[];
  videos: MediaItem[];
  available_for_perennial: boolean;
  status: boolean;
}

const CF_BASE = "https://d37pbainmkhx6o.cloudfront.net/";

function itemDisplayUrl(item: MediaItem): string {
  if (item._displayUrl) return item._displayUrl;
  if (item.baseurl && item.fileservername)
    return `${CF_BASE}${item.baseurl}${item.fileservername}`;
  return item.fileservername ?? "";
}

// ── Rich HTML Editor ───────────────────────────────────────────────────────────

function RichHtmlEditor({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
}) {
  const editorRef = useRef<HTMLDivElement>(null);

  // Sync initial value imperatively to avoid cursor-reset on re-render
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]); // intentionally only on mount / external value change

  const exec = useCallback(
    (cmd: string, val?: string) => {
      document.execCommand(cmd, false, val);
      editorRef.current?.focus();
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    },
    [onChange]
  );

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const btnCls =
    "px-2 py-1 text-xs rounded hover:bg-accent transition-colors select-none";

  return (
    <div
      className="border rounded-md overflow-hidden"
      style={{ borderColor: "hsl(240 20% 22%)" }}
    >
      {/* Toolbar */}
      <div
        className="flex flex-wrap gap-0.5 p-1.5 border-b"
        style={{
          background: "hsl(240 20% 14%)",
          borderColor: "hsl(240 20% 22%)",
        }}
      >
        <button type="button" className={`${btnCls} font-bold`} onClick={() => exec("bold")} title="Bold">B</button>
        <button type="button" className={`${btnCls} italic`} onClick={() => exec("italic")} title="Italic">I</button>
        <button type="button" className={`${btnCls} underline`} onClick={() => exec("underline")} title="Underline">U</button>
        <span className="w-px bg-border mx-1" />
        <button type="button" className={btnCls} onClick={() => exec("formatBlock", "h2")} title="Heading 2">H2</button>
        <button type="button" className={btnCls} onClick={() => exec("formatBlock", "h3")} title="Heading 3">H3</button>
        <button type="button" className={`${btnCls} text-muted-foreground`} onClick={() => exec("formatBlock", "p")} title="Paragraph">P</button>
        <span className="w-px bg-border mx-1" />
        <button type="button" className={btnCls} onClick={() => exec("insertUnorderedList")} title="Bullet list">• List</button>
        <button type="button" className={btnCls} onClick={() => exec("insertOrderedList")} title="Numbered list">1. List</button>
        <span className="w-px bg-border mx-1" />
        <button type="button" className={`${btnCls} text-destructive`} onClick={() => exec("removeFormat")} title="Clear formatting">Clear</button>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        className="min-h-[200px] p-3 text-sm focus:outline-none prose prose-sm max-w-none dark:prose-invert"
        style={{ color: "inherit" }}
        onInput={handleInput}
        onBlur={() => {
          if (editorRef.current) onChange(editorRef.current.innerHTML);
        }}
        aria-label="Blog content editor"
      />
    </div>
  );
}

// ── Media List Field ───────────────────────────────────────────────────────────

function MediaListField({
  label,
  icon: Icon,
  items,
  onChange,
  withTitle,
  withDescription,
  withImgStatus,
  disabled,
}: {
  label: string;
  icon: React.ElementType<{ className?: string }>;
  items: MediaItem[];
  onChange: (items: MediaItem[]) => void;
  withTitle?: boolean;
  withDescription?: boolean;
  withImgStatus?: boolean;
  disabled?: boolean;
}) {
  const [newUrl, setNewUrl] = useState("");

  function addItem() {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    onChange([
      ...items,
      {
        fileservername: trimmed,
        baseurl: "",
        title: "",
        description: "",
        ...(withImgStatus ? { img_status: 0 } : {}),
      },
    ]);
    setNewUrl("");
  }

  function removeItem(idx: number) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function updateItem(idx: number, patch: Partial<MediaItem>) {
    onChange(items.map((item, i) => (i === idx ? { ...item, ...patch } : item)));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-muted-foreground" />
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-xs text-muted-foreground">({items.length})</span>
      </div>

      {/* Existing items */}
      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="rounded-lg border p-3 space-y-2"
              style={{ borderColor: "hsl(240 20% 22%)" }}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">
                    {itemDisplayUrl(item) || "(no URL)"}
                  </p>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="shrink-0 inline-flex size-6 items-center justify-center rounded hover:bg-destructive/15 text-destructive"
                    title="Remove"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>

              {withImgStatus && (
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.img_status === 1}
                    disabled={disabled}
                    onChange={(e) =>
                      updateItem(idx, { img_status: e.target.checked ? 1 : 0 })
                    }
                    className="rounded"
                  />
                  Set as thumbnail
                </label>
              )}

              {withTitle && (
                <Input
                  placeholder="Title (optional)"
                  value={item.title ?? ""}
                  disabled={disabled}
                  onChange={(e) => updateItem(idx, { title: e.target.value })}
                  className="h-7 text-xs"
                />
              )}

              {withDescription && (
                <Input
                  placeholder="Description (optional)"
                  value={item.description ?? ""}
                  disabled={disabled}
                  onChange={(e) =>
                    updateItem(idx, { description: e.target.value })
                  }
                  className="h-7 text-xs"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new item */}
      {!disabled && (
        <div className="flex gap-2">
          <Input
            placeholder={`Enter ${label.toLowerCase()} URL…`}
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
            className="h-8 text-sm flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            className="shrink-0 h-8"
          >
            <Plus className="size-3.5 mr-1" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Blog Form ─────────────────────────────────────────────────────────────────

const BASE_URL =
  process.env.NEXT_PUBLIC_API_CLOUDFRONT ||
  process.env.NEXT_PUBLIC_BACKEND_URL ||
  "https://api.divineinfinitebeing.com/";

export function BlogForm() {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const isEdit = !!id;
  const { data: session } = useSession();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<BlogFormValues>({
    defaultValues: {
      title: "",
      priority: "",
      description: "",
      images: [],
      files: [],
      audios: [],
      videos: [],
      available_for_perennial: false,
      status: true,
    },
  });

  // Fetch existing record on edit
  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["blogmanagement/blog-details-fetch", id],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}blogmanagement/blog-details-fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      // Task 03: Angular reads from response.response.results.res
      return data?.response?.results?.res ?? data?.results?.res ?? data?.results ?? data;
    },
    enabled: isEdit && !!session?.token,
  });

  // Prefill form when record loads
  useEffect(() => {
    if (!record) return;
    const r = record as Record<string, unknown>;

    function toMediaItems(arr: unknown): MediaItem[] {
      if (!Array.isArray(arr)) return [];
      return arr.map((item) => {
        if (typeof item === "string") return { fileservername: item, baseurl: "" };
        return item as MediaItem;
      });
    }

    reset({
      title: String(r.title ?? ""),
      priority: String(r.priority ?? ""),
      description: String(r.description ?? ""),
      images: toMediaItems(r.images),
      files: toMediaItems(r.files),
      audios: toMediaItems(r.audios),
      videos: toMediaItems(r.videos),
      available_for_perennial: r.available_for_perennial === 1 || r.available_for_perennial === true,
      status: r.status === 1 || r.status === true,
    });
  }, [record, reset]);

  const addMutation = useApiPost("blogmanagement/blog-add");
  const editMutation = useApiPost("blogmanagement/blog-edit");
  const mutation = isEdit ? editMutation : addMutation;

  async function onSubmit(values: BlogFormValues) {
    try {
      // Task 03: serialize with correct payload — status/perennial as 0|1, arrays as-is
      const body: Record<string, unknown> = {
        title: values.title,
        description: values.description,
        priority: values.priority ? parseFloat(values.priority) : 0,
        status: values.status ? 1 : 0,
        available_for_perennial: values.available_for_perennial ? 1 : 0,
        images: values.images.map(({ _displayUrl, ...rest }) => rest),
        files: values.files.map(({ _displayUrl, ...rest }) => rest),
        audios: values.audios.map(({ _displayUrl, ...rest }) => rest),
        videos: values.videos.map(({ _displayUrl, ...rest }) => rest),
      };
      if (isEdit) body._id = id;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Blog post updated" : "Blog post created");
      router.push("/admin/blog");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-9 rounded-md" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const pageTitle = isEdit ? "Edit Blog Post" : "Add Blog Post";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <h1 className="text-xl font-semibold">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic fields card */}
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Blog Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            {/* Title + Priority row */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <Input id="title" {...field} placeholder="Blog post title" />
                  )}
                />
                {errors.title && (
                  <p className="text-xs text-destructive">{errors.title.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="priority">
                  Priority <span className="text-destructive">*</span>
                </Label>
                <Controller
                  name="priority"
                  control={control}
                  rules={{ required: "Priority is required" }}
                  render={({ field }) => (
                    <Input id="priority" type="number" {...field} placeholder="1" />
                  )}
                />
                {errors.priority && (
                  <p className="text-xs text-destructive">{errors.priority.message}</p>
                )}
              </div>
            </div>

            {/* Rich description */}
            <div className="space-y-1.5">
              <Label>
                Content <span className="text-destructive">*</span>
              </Label>
              <Controller
                name="description"
                control={control}
                rules={{ required: "Content is required" }}
                render={({ field }) => (
                  <RichHtmlEditor
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>

            {/* Switches row */}
            <div className="flex flex-wrap gap-6">
              <Controller
                name="available_for_perennial"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <div
                      role="switch"
                      aria-checked={field.value}
                      tabIndex={0}
                      onClick={() => field.onChange(!field.value)}
                      onKeyDown={(e) => e.key === " " && field.onChange(!field.value)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                        field.value ? "bg-orange-500" : "bg-muted"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform ${
                          field.value ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </div>
                    <span className="text-sm">Perennial Mandalism Only</span>
                  </label>
                )}
              />

              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <div
                      role="switch"
                      aria-checked={field.value}
                      tabIndex={0}
                      onClick={() => field.onChange(!field.value)}
                      onKeyDown={(e) => e.key === " " && field.onChange(!field.value)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                        field.value ? "bg-orange-500" : "bg-muted"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform ${
                          field.value ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </div>
                    <span className="text-sm">Published</span>
                  </label>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Media card */}
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Media
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-7">
            {/* Images */}
            <Controller
              name="images"
              control={control}
              render={({ field }) => (
                <MediaListField
                  label="Images"
                  icon={ImageIcon}
                  items={field.value}
                  onChange={field.onChange}
                  withImgStatus
                />
              )}
            />

            <div className="border-t" style={{ borderColor: "hsl(240 20% 20%)" }} />

            {/* Files */}
            <Controller
              name="files"
              control={control}
              render={({ field }) => (
                <MediaListField
                  label="Files"
                  icon={FileIcon}
                  items={field.value}
                  onChange={field.onChange}
                  withTitle
                  withDescription
                />
              )}
            />

            <div className="border-t" style={{ borderColor: "hsl(240 20% 20%)" }} />

            {/* Audios */}
            <Controller
              name="audios"
              control={control}
              render={({ field }) => (
                <MediaListField
                  label="Audios"
                  icon={Music}
                  items={field.value}
                  onChange={field.onChange}
                  withTitle
                  withDescription
                />
              )}
            />

            <div className="border-t" style={{ borderColor: "hsl(240 20% 20%)" }} />

            {/* Videos */}
            <Controller
              name="videos"
              control={control}
              render={({ field }) => (
                <MediaListField
                  label="Videos"
                  icon={Video}
                  items={field.value}
                  onChange={field.onChange}
                  withTitle
                  withDescription
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Submit row */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mutation.isPending ? "Saving…" : isEdit ? "Update Post" : "Create Post"}
          </button>
          <Button type="button" variant="outline" onClick={() => router.back()}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
